import React from 'react';
import {
  Card,
  Typography,
  Button,
  Tag,
  Flex,
  Row,
  Col,
  Tooltip,
  Alert,
} from 'antd';
import { useTranslation } from '@refinedev/core';
import { ChangeReq, PostReq } from '@/api/types/getOnePost';
import { getFileUrl } from '@/Helpers';
import {
  EyeOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TagOutlined,
  CommentOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatAmmanDateTime } from '@/utils/formatAmmanDateTime';
import { CustomAudioPlayer } from '@/components/audioPlayer/CustomAudioPlayer';
import { VideoPlayer } from '@/components/videoPlayer/VideoPlayer';
import { ChangeRequestBadge } from '../badges';
import { isReviewableStatus } from '@/utils/requestPermissions';

const { Title, Text, Paragraph } = Typography;

interface PostInfoCardProps {
  postReq: PostReq | undefined;
  onAddComment: (sectionId?: number) => void;
  isLoading?: boolean;
  onChangeRequest: (changeRequests: ChangeReq[]) => void;
  showChangeRequestActions?: boolean;
}

export const PostInfoCard: React.FC<PostInfoCardProps> = ({
  postReq,
  onAddComment,
  isLoading,
  onChangeRequest,
  showChangeRequestActions = true,
}) => {
  const { translate } = useTranslation();
  return (
    <Card
      className="mb-6 shadow-sm hover:shadow-md transition-shadow duration-300"
      loading={isLoading}
      extra={
        <div className="flex flex-wrap items-center gap-2 p-1">
          {showChangeRequestActions &&
            (isReviewableStatus(postReq?.requests?.status) ||
              postReq?.requests?.status === 'CHANGE_REQUESTED') && (
              <Button
                type="primary"
                icon={<CommentOutlined />}
                onClick={() => onAddComment()}
                className="flex items-center"
              >
                {translate('comments.add')}
              </Button>
            )}
          <Tooltip title={translate('posts.viewPeriod')}>
            <Tag icon={<ClockCircleOutlined />} color="blue">
              {postReq?.view_period}
            </Tag>
          </Tooltip>
          {showChangeRequestActions && (
            <ChangeRequestBadge
              changeRequests={postReq?.change_requests?.filter(
                (cr) => cr.status === 'PENDING'
              )}
              onClick={() => onChangeRequest(postReq?.change_requests || [])}
            />
          )}
        </div>
      }
    >
      <Title level={2} className="mb-6">
        {postReq?.title}
      </Title>
      {postReq?.requests?.assigned_admin && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message={
            <span>
              {translate('listing-request.assignedAdmin.title')}:{' '}
              <strong>{postReq.requests.assigned_admin.name}</strong>
              {' — '}
              {translate(
                `listing-admins.role.${postReq.requests.assigned_admin.admin_type}`
              )}
              {postReq.requests.assigned_at && (
                <>
                  {' · '}
                  {translate('listing-request.assignedAt.title')}:{' '}
                  {formatAmmanDateTime(postReq.requests.assigned_at)}
                </>
              )}
            </span>
          }
        />
      )}
      <Flex vertical gap="middle" className="mb-6">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Flex align="center" gap="small">
              <EnvironmentOutlined className="text-secondary" />
              <Text strong>{translate('posts.location')}:</Text>
              <Text>{postReq?.location}</Text>
            </Flex>
          </Col>
          <Col xs={24} sm={8}>
            <Flex align="center" gap="small">
              <CalendarOutlined className="text-secondary" />
              <Text strong>{translate('posts.date')}:</Text>
              <Text>{dayjs(postReq?.date).format('YYYY-MM-DD')}</Text>
            </Flex>
          </Col>
          <Col xs={24} sm={8}>
            <Flex align="center" gap="small">
              <TagOutlined className="text-secondary" />
              <Text strong>{translate('posts.type')}:</Text>
              <Tag color="blue" className="px-3 py-1 uppercase">
                {postReq?.type}
              </Tag>
            </Flex>
          </Col>
        </Row>
        {postReq?.summary && (
          <div className="border rounded-lg border-blue-500  p-6 rounded-r-lg">
            <Title level={5} className="mb-2 flex items-center gap-2">
              <EyeOutlined className="text-secondary" />
              {translate('posts.summary')}
            </Title>
            <Paragraph className="text-base mb-0">{postReq.summary}</Paragraph>
          </div>
        )}
        {postReq?.main_audio && (
          <Card className="mb-6 ">
            <Title level={5} className="mb-4 flex items-center gap-2 text-3xl">
              <PlayCircleOutlined className="text-secondary" />
              {translate('posts.mainAudio')}
            </Title>
            <CustomAudioPlayer
              audioUrl={getFileUrl(postReq.main_audio.id.toString())}
              className="w-full"
            />
            {postReq.main_audio.description && (
              <div className="text-sm text-gray-500 mt-2">
                {postReq.main_audio.description}
              </div>
            )}
          </Card>
        )}
        {postReq?.main_video_id && (
          <Card className="mb-6">
            <Title level={5} className="mb-4 flex items-center gap-2 text-3xl">
              <PlayCircleOutlined className="text-secondary" />
              {translate('posts.mainVideo')}
            </Title>
            <VideoPlayer
              uploadId={postReq.main_video_id}
              fileName={postReq.main_video?.file_name}
              eager
            />
            {postReq.main_video.description && (
              <div className="text-sm text-gray-500">
                {postReq.main_video.description}
              </div>
            )}
          </Card>
        )}
      </Flex>
    </Card>
  );
};
